import JoinQueryCard from "./JoinQueryCard";
import WhereFiltersCard from "./filters/WhereFiltersCard";
import JoinIcon from "./icons/JoinIcon";
import { PgTypeIcon } from "./icons/PgTypeIcon";
import { DatabaseConnectionSchema, DecryptedConnection } from "@avenueops/shared-types";
import {
  Column,
  JoinOnOperator,
  JoinType,
  PostgresQueryCompiler,
  QueryAst,
  QueryJoin,
  QueryTable,
  WhereBooleanType,
  WhereFilterValueType,
  WhereOperator,
} from "@avenueops/visual-query-editor";
import { Button, OptionGroup, SmartForm, SteppedDropdown } from "@avenueops/web-components";
import { DevTool } from "@hookform/devtools";
import { TableIcon } from "@radix-ui/react-icons";
import { FilterIcon } from "components/molecules/filters/FilterIcon";
import QueryPreviews from "components/molecules/queryBuilder/QueryPreviews";
import { useQueryRunResults } from "context/QueryRunContext";
import { useEffect, useState } from "react";
import { Controller, FieldErrors, useFieldArray, useFormContext, useWatch } from "react-hook-form";
import { trpc, trpc2 } from "utils/trpc";

export interface VQEProps {
  prefix: string;
  datasourceIdName: string;
  connections: (DecryptedConnection & { schema: { id: string } | null })[];
}

function VisualQueryEditor({ prefix, datasourceIdName, connections }: VQEProps) {
  const { mutateAsync: previewQuery, data: previewData, isLoading: isLoadingPreview } = trpc.dataviews.preview.useMutation();
  const { queryRunResults, setQueryRunResults } = useQueryRunResults();
  const [previewVisible, setPreviewVisible] = useState(false);
  const formMethods = useFormContext();

  const formData = useWatch({ control: formMethods.control });
  const databaseConnectionId = useWatch({ name: "databaseId", control: formMethods.control });
  formMethods.register(`${prefix}.where`);
  const joinsFieldArray = useFieldArray({
    control: formMethods.control,
    name: `${prefix}.joins`,
  });

  const datasourceId = useWatch({ name: datasourceIdName, control: formMethods.control });
  const primaryTable = useWatch({ name: `${prefix}.primaryTable`, control: formMethods.control }) as QueryTable;
  // The id field gets added by the field array to be used in "key" props.
  const joins = useWatch({ name: `${prefix}.joins`, control: formMethods.control }) as (QueryJoin & { id: string })[];
  const whereClause = useWatch({ name: `${prefix}.where`, control: formMethods.control });

  const connection = connections?.find((connection) => connection.id === datasourceId);

  const { data: connectionSchemaObj, isLoading: isLoadingSchema } = trpc2.v2.databaseConnections.getSchema.useQuery(
    {
      connectionId: connection?.id ?? "",
    },
    {
      enabled: !!connection,
    },
  );

  const connectionSchema = connectionSchemaObj?.tables as unknown as DatabaseConnectionSchema | undefined;
  const tableOptions =
    connections && connectionSchema
      ? {
          optionKey: "name" as keyof QueryTable,
          options: connectionSchema.map((table) => {
            return {
              label: table.table_name,
              value: table.table_name,
              icon: <TableIcon className="w-4 h-4 mr-2" />,
              metaData: {
                schemaName: table.table_schema,
              },
              subOptions: {
                multiSelect: true,
                optionKey: "columns" as keyof QueryTable,
                options: table.columns.map((column) => ({
                  label: column.column_name,
                  value: column.column_name,
                  metaData: {
                    data_type: column.data_type,
                    enum_name: column.enum_name,
                  },
                })),
              },
            };
          }),
        }
      : null;

  const availableTables = [...(primaryTable ? [primaryTable] : []), ...(joins ? joins?.map((join) => join.table) : [])] as QueryTable[];
  const columnOptions = availableTables.reduce((acc, table) => {
    return [
      ...acc,
      {
        label: table.name,
        options: table.columns.map((column) => ({
          label: column.name,
          value: column.name,
          icon: <PgTypeIcon pgType={column.type} className="w-4 h-4" />,
          meta: {
            data_type: column.type,
            tableName: table.name,
            schemaName: table.schemaName,
            enumName: column.enumName,
          },
        })),
      },
    ];
  }, [] as OptionGroup[]);

  useEffect(() => {
    if (previewData) {
      setQueryRunResults(previewData.resultRows);
    }
  }, [previewData]);

  return (
    <div className="flex flex-col items-center justify-center overflow-auto">
      <DevTool control={formMethods.control} />
      <div className="min-w-[300px] flex flex-col gap-5">
        <div className="md:w-[600px] flex justify-end text-start py-5 border-b border-slate-100">
          <div className="w-[180px]">
            <SmartForm.DropdownSelect
              size="SMALL"
              name={datasourceIdName}
              options={
                connections?.map((connection) => ({
                  label: connection.displayName ?? "Database Connection",
                  value: connection.id,
                })) ?? []
              }
            />
          </div>
        </div>
        {tableOptions && tableOptions?.options ? (
          <div className="py-5 border-b border-slate-100 grid sm:grid-cols-5 space-x-5">
            <div className="flex flex-col ml-5 sm:ml-0 sm:items-end">
              <p className="mr-5">Data</p>
              <div className="flex-1 border-r border-slate-200" />
            </div>
            <div className="col-span-4">
              <div className="w-[300px] pb-5">
                <Controller
                  name={`${prefix}.primaryTable`}
                  control={formMethods.control}
                  render={({ field, fieldState }) => {
                    return (
                      <SteppedDropdown<{
                        name: string;
                        columns: any[];
                      }>
                        onBlur={field.onBlur}
                        error={fieldState.error as unknown as FieldErrors}
                        name={`${prefix}.primaryTable`}
                        options={tableOptions as any} // TODO fix this type
                        defaultValue={{
                          name: field.value?.name,
                          columns: field.value?.columns?.map((column: Column) => column.name),
                        }}
                        value={{
                          name: field.value?.name,
                          columns: field.value?.columns?.map((column: Column) => column.name),
                        }}
                        onChange={(val, options) => {
                          const tableOption = options?.name as unknown as { value: string; metaData: { schemaName: string } };
                          const columnOptions = options?.columns as unknown as
                            | { value: string; metaData: { data_type: string; enum_name?: string } }[]
                            | undefined;
                          field.onChange({
                            name: val.name,
                            schemaName: tableOption?.metaData?.schemaName,
                            columns:
                              columnOptions?.map((column) => ({
                                name: column.value,
                                schemaName: tableOption?.metaData?.schemaName,
                                tableName: val.name,
                                enumName: column.metaData?.enum_name,
                                type: column.metaData?.data_type ?? "unknown",
                              })) ?? [],
                          });
                        }}
                        title={
                          <div className="flex items-center">
                            <TableIcon className="w-4 h-4 mr-2" />
                            <span>{primaryTable?.name ?? "Primary Table"}</span>
                          </div>
                        }
                      />
                    );
                  }}
                />
              </div>
            </div>
            {joins && joins.length > 0 ? (
              <>
                <div className="flex flex-col sm:items-end">
                  <p className="mr-5">Join Data</p>
                  <div className="flex-1 border-r border-slate-200" />
                </div>
                <div className="col-span-4">
                  <div className="w-[485px] flex flex-col pb-5 gap-5">
                    {joinsFieldArray.fields?.map((field, index) => (
                      <JoinQueryCard
                        key={field.id}
                        index={index}
                        prefix={prefix}
                        tableOptions={tableOptions as any} // TODO fix this type
                        columnOptions={columnOptions}
                        removeSelf={() => {
                          joinsFieldArray.remove(index);
                        }}
                      />
                    ))}
                  </div>
                </div>
              </>
            ) : null}
            {whereClause ? (
              <>
                <div className="flex flex-col sm:items-end">
                  <p className="mr-5">Filter Data</p>
                  <div className="flex-1 border-r border-slate-200" />
                </div>
                <div className="col-span-4">
                  <div className="w-[485px] flex flex-col pb-5 gap-5">
                    <WhereFiltersCard prefix={prefix} columnOptions={columnOptions} connectionSchema={connectionSchema} />
                  </div>
                </div>
              </>
            ) : null}
            <div />
            {primaryTable && primaryTable.name ? (
              <div className="col-span-4 sm:pr-8 flex pt-2 gap-2 items-center justify-center">
                <Button
                  type="TERTIARY"
                  size="SMALL"
                  onClick={() => {
                    joinsFieldArray.append({
                      type: JoinType.INNER,
                      table: {
                        name: undefined,
                        columns: [],
                      },
                      on: [
                        {
                          left: undefined,
                          operator: JoinOnOperator.EQUALS,
                          right: undefined,
                        },
                      ],
                    });
                  }}
                >
                  <JoinIcon className="w-5 h-5" />
                  <p>Join</p>
                </Button>
                {!whereClause ? (
                  <Button
                    type="TERTIARY"
                    size="SMALL"
                    onClick={() => {
                      formMethods.setValue(`${prefix}.where`, {
                        type: WhereBooleanType.AND,
                        filters: [
                          {
                            left: null,
                            operator: WhereOperator.EQUALS,
                            right: null,
                          },
                        ],
                      });
                    }}
                  >
                    <FilterIcon />
                    <p>Filter</p>
                  </Button>
                ) : null}
              </div>
            ) : null}
          </div>
        ) : null}
      </div>
      <div className="w-full p-10 flex flex-col gap-5">
        <hr className="w-full" />
        <div className="w-full flex justify-end">
          <Button
            submitting={isLoadingPreview}
            onClick={async () => {
              const isValid = await formMethods.trigger("visualQueryBlob");
              if (!isValid) {
                return;
              }
              const VisQuery = new PostgresQueryCompiler(formData.visualQueryBlob as QueryAst);
              const query = VisQuery.compile();

              previewQuery({ databaseId: databaseConnectionId!, query }).then(() => {
                setPreviewVisible(true);
              });
            }}
          >
            Run
          </Button>
        </div>
        <QueryPreviews className="bottom-0 left-5 right-5" previewOpen={previewVisible} setPreviewOpen={setPreviewVisible} transparentBackground />
      </div>
    </div>
  );
}

export default VisualQueryEditor;
